import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import get from "lodash/get";
import { ChangeEvent, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Helmet } from "react-helmet-async";
import { useMutation, useQuery } from "react-query";
import useNotification from "../hooks/useNotification";
import divisionService from "../services/division";
import mysteryShopperService from "../services/mysteryShopper";

type BooleanChoice = "" | "true" | "false";
type PointChoice = "" | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

type DivisionOption = {
  id: number;
  name: string;
};

type FormValues = {
  shopper_name: string;
  shopper_email: string;
  shopper_phone: string;
  visit_date: string;
  visit_time: string;
  division_id: number | null;

  kerapihan_crew: PointChoice;
  catatan_grooming: string;

  penawaran_store: BooleanChoice;
  crew_menyapa: BooleanChoice;
  keramahan: PointChoice;
  komunikasi: PointChoice;
  responsiveness: PointChoice;
  fokus_customer: PointChoice;
  attitude: PointChoice;
  catatan_behavior: string;

  penyambutan: BooleanChoice;
  penjelasan: PointChoice;
  bantuan: BooleanChoice;
  kejelasan_arahan: PointChoice;
  closing: BooleanChoice;
  catatan_service_flow: string;

  pengetahuan_paket_harga: PointChoice;
  menawarkan_loyalty_event: BooleanChoice;
  menjelaskan_fitur: PointChoice;
  menawarkan_additional_print: BooleanChoice;
  kejelasan_informasi: PointChoice;
  catatan_product_knowledge: string;

  kebersihan_lantai: PointChoice;
  kebersihan_booth: PointChoice;
  kebersihan_properti: PointChoice;
  kenyamanan_tempat: PointChoice;
  musik: BooleanChoice;
  catatan_kondisi_store: string;

  mesin_normal: PointChoice;
  kamera_jelas: PointChoice;
  printer_hasil: PointChoice;
  error: BooleanChoice;
  catatan_equipment: string;

  penjelasan_flow_antrian: PointChoice;
  waktu_tunggu: PointChoice;
  kerapihan_antrian: PointChoice;
  pengaturan_flow: PointChoice;
  catatan_antrian: string;

  kepuasan_keseluruhan: BooleanChoice;
  kembali_lagi: BooleanChoice;
  rekomendasi: BooleanChoice;

  red_flag: string;
  red_flag_other: string;

  cerita_pengalaman: string;

  foto_crew: File[];
  foto: File[];
  voice_additional: File[];
  video_jingle: File[];
};

const totalSections = 12;
const pointOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const redFlagOptions = [
  "Crew tidak sopan",
  "Tidak dibantu",
  "Alat bermasalah",
  "Tempat kotor",
  "Pelayanan Buruk",
  "Other",
];

const requiredInitialFields: (keyof FormValues)[] = [
  "shopper_name",
  "shopper_email",
  "shopper_phone",
  "visit_date",
  "visit_time",
  "division_id",
];

const requiredPointAndBooleanFieldsBySection: Record<number, (keyof FormValues)[]> = {
  1: ["kerapihan_crew"],
  2: [
    "penawaran_store",
    "crew_menyapa",
    "keramahan",
    "komunikasi",
    "responsiveness",
    "fokus_customer",
    "attitude",
  ],
  3: ["penyambutan", "penjelasan", "bantuan", "kejelasan_arahan", "closing"],
  4: [
    "pengetahuan_paket_harga",
    "menawarkan_loyalty_event",
    "menjelaskan_fitur",
    "menawarkan_additional_print",
    "kejelasan_informasi",
  ],
  5: [
    "kebersihan_lantai",
    "kebersihan_booth",
    "kebersihan_properti",
    "kenyamanan_tempat",
    "musik",
  ],
  6: ["mesin_normal", "kamera_jelas", "printer_hasil", "error"],
  7: [
    "penjelasan_flow_antrian",
    "waktu_tunggu",
    "kerapihan_antrian",
    "pengaturan_flow",
  ],
  8: ["kepuasan_keseluruhan", "kembali_lagi", "rekomendasi"],
};

const allRequiredPointAndBooleanFields: (keyof FormValues)[] =
  Object.values(requiredPointAndBooleanFieldsBySection).flat();

const requiredFieldsBySection: Record<number, (keyof FormValues)[]> = {
  0: requiredInitialFields,
  1: [...requiredPointAndBooleanFieldsBySection[1], "catatan_grooming"],
  2: [...requiredPointAndBooleanFieldsBySection[2], "catatan_behavior"],
  3: [...requiredPointAndBooleanFieldsBySection[3], "catatan_service_flow"],
  4: [...requiredPointAndBooleanFieldsBySection[4], "catatan_product_knowledge"],
  5: [...requiredPointAndBooleanFieldsBySection[5], "catatan_kondisi_store"],
  6: [...requiredPointAndBooleanFieldsBySection[6], "catatan_equipment"],
  7: [...requiredPointAndBooleanFieldsBySection[7], "catatan_antrian"],
  8: requiredPointAndBooleanFieldsBySection[8],
  9: ["red_flag"],
  10: ["cerita_pengalaman"],
};

function buildPayload(values: FormValues): FormData {
  const payload = new FormData();

  Object.entries(values).forEach(([key, value]) => {
    if (
      key === "foto_crew" ||
      key === "foto" ||
      key === "voice_additional" ||
      key === "video_jingle"
    ) {
      const files = value as File[];
      files.forEach((file) => payload.append(key, file));
      return;
    }

    if (key === "red_flag_other" && values.red_flag !== "Other") {
      return;
    }

    if (value === "" || value === null || typeof value === "undefined") {
      return;
    }

    if (value === "true" || value === "false") {
      payload.append(key, value);
      return;
    }

    payload.append(key, String(value));
  });

  return payload;
}

export default function MysteryShopperPublicPage() {
  const notification = useNotification();
  const [currentSection, setCurrentSection] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: {
      shopper_name: "",
      shopper_email: "",
      shopper_phone: "",
      visit_date: "",
      visit_time: "",
      division_id: null,

      kerapihan_crew: "",
      catatan_grooming: "",

      penawaran_store: "",
      crew_menyapa: "",
      keramahan: "",
      komunikasi: "",
      responsiveness: "",
      fokus_customer: "",
      attitude: "",
      catatan_behavior: "",

      penyambutan: "",
      penjelasan: "",
      bantuan: "",
      kejelasan_arahan: "",
      closing: "",
      catatan_service_flow: "",

      pengetahuan_paket_harga: "",
      menawarkan_loyalty_event: "",
      menjelaskan_fitur: "",
      menawarkan_additional_print: "",
      kejelasan_informasi: "",
      catatan_product_knowledge: "",

      kebersihan_lantai: "",
      kebersihan_booth: "",
      kebersihan_properti: "",
      kenyamanan_tempat: "",
      musik: "",
      catatan_kondisi_store: "",

      mesin_normal: "",
      kamera_jelas: "",
      printer_hasil: "",
      error: "",
      catatan_equipment: "",

      penjelasan_flow_antrian: "",
      waktu_tunggu: "",
      kerapihan_antrian: "",
      pengaturan_flow: "",
      catatan_antrian: "",

      kepuasan_keseluruhan: "",
      kembali_lagi: "",
      rekomendasi: "",

      red_flag: "",
      red_flag_other: "",

      cerita_pengalaman: "",

      foto_crew: [],
      foto: [],
      voice_additional: [],
      video_jingle: [],
    },
    shouldUnregister: false,
  });

  const { control, handleSubmit, setError, watch } = form;

  const divisionQuery = useQuery("public-division-list", () =>
    divisionService.getPublicDivisionList()
  );

  const divisionOptions = useMemo(() => {
    const data = get(divisionQuery.data, "data", []);
    return Array.isArray(data) ? (data as DivisionOption[]) : [];
  }, [divisionQuery.data]);

  const submitMutation = useMutation(
    "create-public-mystery-shopper",
    mysteryShopperService.createPublicMysteryShopper,
    {
      onSuccess: () => {
        setIsSubmitted(true);
      },
      onError: () => {
        notification.onOpen({
          message: "Gagal mengirim form. Silakan coba lagi.",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const redFlag = watch("red_flag");
  const fotoCrew = watch("foto_crew");
  const fotoStore = watch("foto");
  const voiceAdditional = watch("voice_additional");
  const videoJingle = watch("video_jingle");

  const onSubmit = (values: FormValues) => {
    if (values.foto_crew.length > 5) {
      setError("foto_crew", {
        type: "manual",
        message: "Maksimal 5 foto crew.",
      });
      setCurrentSection(11);
      return;
    }

    if (values.foto.length > 10) {
      setError("foto", {
        type: "manual",
        message: "Maksimal 10 foto.",
      });
      setCurrentSection(11);
      return;
    }

    const hasInvalidPointOrBoolean = allRequiredPointAndBooleanFields.some(
      (fieldName) => {
        const currentValue = values[fieldName];
        return currentValue === "" || currentValue === null || typeof currentValue === "undefined";
      }
    );

    const hasInvalidInitialFields = requiredInitialFields.some((fieldName) => {
      const currentValue = values[fieldName];
      return currentValue === "" || currentValue === null || typeof currentValue === "undefined";
    });

    const hasInvalidTextFields = [
      "catatan_grooming",
      "catatan_behavior",
      "catatan_service_flow",
      "catatan_product_knowledge",
      "catatan_kondisi_store",
      "catatan_equipment",
      "catatan_antrian",
      "cerita_pengalaman",
      "red_flag",
    ].some((fieldName) => {
      const currentValue = values[fieldName as keyof FormValues];
      return currentValue === "" || currentValue === null || typeof currentValue === "undefined";
    });

    const hasMissingDocumentation =
      values.foto_crew.length === 0 ||
      values.foto.length === 0 ||
      values.voice_additional.length === 0 ||
      values.video_jingle.length === 0;

    if (hasInvalidInitialFields) {
      notification.onOpen({
        message: "Lengkapi Nama Shopper, Email Shopper, No HP Shopper, Tanggal Kunjungan, dan Lokasi Cabang.",
        type: "error",
        position: "top",
      });
      return;
    }

    if (hasInvalidPointOrBoolean) {
      notification.onOpen({
        message: "Lengkapi semua nilai point dan pilihan Ya/Tidak terlebih dahulu.",
        type: "error",
        position: "top",
      });
      return;
    }

    if (hasInvalidTextFields || (values.red_flag === "Other" && !values.red_flag_other)) {
      notification.onOpen({
        message: "Semua field catatan, red flag, dan cerita pengalaman wajib diisi.",
        type: "error",
        position: "top",
      });
      return;
    }

    if (hasMissingDocumentation) {
      notification.onOpen({
        message: "Semua dokumen wajib diupload (Foto Crew, Foto Store, Voice, dan Video Jingle).",
        type: "error",
        position: "top",
      });
      return;
    }

    const payload = buildPayload(values);
    submitMutation.mutate(payload);
  };

  const goNext = async () => {
    const fieldsToValidate = requiredFieldsBySection[currentSection] || [];
    if (fieldsToValidate.length > 0) {
      const valid = await form.trigger(fieldsToValidate);
      if (!valid) {
        notification.onOpen({
          message:
            currentSection === 0
              ? "Nama Shopper, Email Shopper, No HP Shopper, Tanggal Kunjungan, dan Lokasi Cabang wajib diisi."
              : "Field point dan pilihan Ya/Tidak wajib diisi sebelum lanjut.",
          type: "error",
          position: "top",
        });
        return;
      }
    }

    if (currentSection === 9 && form.getValues("red_flag") === "Other") {
      const validOther = await form.trigger("red_flag_other");
      if (!validOther) {
        notification.onOpen({
          message: "Field Other wajib diisi.",
          type: "error",
          position: "top",
        });
        return;
      }
    }

    if (currentSection === 11) {
      const values = form.getValues();
      if (
        values.foto_crew.length === 0 ||
        values.foto.length === 0 ||
        values.voice_additional.length === 0 ||
        values.video_jingle.length === 0
      ) {
        notification.onOpen({
          message: "Semua upload pada Dokumentasi wajib diisi.",
          type: "error",
          position: "top",
        });
        return;
      }
    }

    setCurrentSection((prev) => Math.min(prev + 1, totalSections - 1));
  };

  const goBack = () => {
    setCurrentSection((prev) => Math.max(prev - 1, 0));
  };

  const renderPointField = (name: keyof FormValues, label: string) => (
    <FormControl fullWidth key={name as string}>
      <FormLabel sx={{ fontWeight: 500, mb: 1 }}>{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        rules={{ required: "Wajib diisi" }}
        render={({ field, fieldState }) => (
          <Box>
            <RadioGroup
              row
              value={field.value ?? ""}
              onChange={(event) => field.onChange(Number(event.target.value))}
              sx={{ gap: 0.5, flexWrap: "wrap" }}
            >
              {pointOptions.map((score) => {
                const isSelected = field.value === score;
                return (
                  <FormControlLabel
                    key={score}
                    value={score}
                    control={<Radio sx={{ display: "none" }} />}
                    label={
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 1,
                          border: "1.5px solid",
                          borderColor: isSelected ? "primary.main" : "grey.400",
                          bgcolor: isSelected ? "primary.main" : "transparent",
                          color: isSelected ? "primary.contrastText" : "text.primary",
                          fontWeight: isSelected ? 700 : 400,
                          fontSize: "0.875rem",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: isSelected ? "primary.dark" : "primary.50",
                          },
                        }}
                      >
                        {score}
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />
                );
              })}
            </RadioGroup>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, px: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                1 = Kurang Baik
              </Typography>
              <Typography variant="caption" color="text.secondary">
                10 = Sangat Baik
              </Typography>
            </Box>
            {fieldState.error && (
              <Typography variant="caption" color="error">
                {fieldState.error.message}
              </Typography>
            )}
          </Box>
        )}
      />
    </FormControl>
  );

  const renderBooleanField = (name: keyof FormValues, label: string) => (
    <FormControl fullWidth key={name as string}>
      <FormLabel>{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        rules={{ required: "Wajib diisi" }}
        render={({ field, fieldState }) => (
          <Box sx={{ mt: 1 }}>
            <RadioGroup
              row
              value={field.value ?? ""}
              onChange={(event) => field.onChange(event.target.value)}
            >
              <FormControlLabel value="true" control={<Radio size="small" />} label="Ya" />
              <FormControlLabel value="false" control={<Radio size="small" />} label="Tidak" />
            </RadioGroup>
            {fieldState.error && (
              <Typography variant="caption" color="error">
                {fieldState.error.message}
              </Typography>
            )}
          </Box>
        )}
      />
    </FormControl>
  );

  const renderTextField = (
    name: keyof FormValues,
    label: string,
    maxLength: number,
    multiline = false,
    rows = 3,
    required = true
  ) => (
    <FormControl fullWidth key={name as string}>
      <FormLabel>{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        rules={required ? { required: "Wajib diisi" } : undefined}
        render={({ field, fieldState }) => (
          <TextField
            size="small"
            sx={{ mt: 1 }}
            {...field}
            multiline={multiline}
            rows={multiline ? rows : undefined}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
            inputProps={{
              maxLength,
            }}
          />
        )}
      />
    </FormControl>
  );

  const handleFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    fieldName: "foto_crew" | "foto" | "voice_additional" | "video_jingle",
    maxFile: number
  ) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length > maxFile) {
      setError(fieldName, {
        type: "manual",
        message: `Maksimal ${maxFile} file.`,
      });
      return;
    }

    form.setValue(fieldName, files, { shouldValidate: true });
  };

  const sectionTitle = [
    "Informasi Kunjungan",
    "Grooming Crew (Penampilan & Kerapihan)",
    "Behavior Crew",
    "Service Flow",
    "Product Knowledge",
    "Store Cleanliness & Ambience",
    "Equipment",
    "Waiting Experience (Antrian)",
    "Overall Experience",
    "Red Flag",
    "Cerita Pengalaman",
    "Dokumentasi",
  ][currentSection];

  const renderSectionContent = () => {
    if (currentSection === 0) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel required>Nama Mistery Shopper</FormLabel>
              <Controller
                name="shopper_name"
                control={control}
                rules={{ required: "Wajib diisi" }}
                render={({ field, fieldState }) => (
                  <TextField
                    size="small"
                    sx={{ mt: 1 }}
                    {...field}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 100 }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel required>Email Shopper</FormLabel>
              <Controller
                name="shopper_email"
                control={control}
                rules={{
                  required: "Wajib diisi",
                  pattern: {
                    value: /^\S+@\S+\.\S+$/,
                    message: "Format email tidak valid",
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    type="email"
                    size="small"
                    sx={{ mt: 1 }}
                    {...field}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 100 }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel required>No HP Shopper</FormLabel>
              <Controller
                name="shopper_phone"
                control={control}
                rules={{
                  required: "Wajib diisi",
                  pattern: {
                    value: /^[0-9+\-()\s]{8,20}$/,
                    message: "Format nomor telepon tidak valid",
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    type="tel"
                    size="small"
                    sx={{ mt: 1 }}
                    {...field}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 20 }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel required>Tanggal Kunjungan</FormLabel>
              <Controller
                name="visit_date"
                control={control}
                rules={{ required: "Wajib diisi" }}
                render={({ field, fieldState }) => (
                  <TextField
                    type="date"
                    size="small"
                    sx={{ mt: 1 }}
                    {...field}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel>Jam Kunjungan</FormLabel>
              <Controller
                name="visit_time"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="time"
                    size="small"
                    sx={{ mt: 1 }}
                    {...field}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel required>Lokasi Cabang</FormLabel>
              <Controller
                name="division_id"
                control={control}
                rules={{ required: "Wajib diisi" }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    sx={{ mt: 1 }}
                    options={divisionOptions}
                    loading={divisionQuery.isLoading}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={
                      divisionOptions.find((option) => option.id === field.value) ||
                      null
                    }
                    onChange={(_, option) => {
                      field.onChange(option ? option.id : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Pilih lokasi cabang"
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      );
    }

    if (currentSection === 1) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>{renderPointField("kerapihan_crew", "Kerapihan Crew")}</Grid>
          <Grid item xs={12}>
            {renderTextField("catatan_grooming", "Catatan Grooming", 500, true)}
          </Grid>
        </Grid>
      );
    }

    if (currentSection === 2) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>{renderBooleanField("penawaran_store", "Penawaran Crew depan Store")}</Grid>
          <Grid item xs={12}>{renderBooleanField("crew_menyapa", "Crew Menyapa Saat Masuk")}</Grid>
          <Grid item xs={12}>{renderPointField("keramahan", "Keramahan (Senyum, Eye Contact)")}</Grid>
          <Grid item xs={12}>{renderPointField("komunikasi", "Cara Komunikasi (Sopan & Jelas)")}</Grid>
          <Grid item xs={12}>{renderPointField("responsiveness", "Responsiveness (Cepat Tanggap)")}</Grid>
          <Grid item xs={12}>{renderPointField("fokus_customer", "Fokus ke Customer (Tidak Sibuk Sendiri)")}</Grid>
          <Grid item xs={12}>{renderPointField("attitude", "Attitude Overall (Friendly & Fun)")}</Grid>
          <Grid item xs={12}>
            {renderTextField("catatan_behavior", "Catatan Behavior", 500, true)}
          </Grid>
        </Grid>
      );
    }

    if (currentSection === 3) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>{renderBooleanField("penyambutan", "Penyambutan saat Masuk")}</Grid>
          <Grid item xs={12}>{renderPointField("penjelasan", "Penjelasan Cara Penggunaan")}</Grid>
          <Grid item xs={12}>{renderBooleanField("bantuan", "Bantuan selama proses (Ditolong Sebelum Diminta)")}</Grid>
          <Grid item xs={12}>{renderPointField("kejelasan_arahan", "Kejelasan Arahan")}</Grid>
          <Grid item xs={12}>{renderBooleanField("closing", "Closing (Terimakasih, dll)")}</Grid>
          <Grid item xs={12}>
            {renderTextField("catatan_service_flow", "Catatan Service Flow", 500, true)}
          </Grid>
        </Grid>
      );
    }

    if (currentSection === 4) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderPointField("pengetahuan_paket_harga", "Pengetahuan Paket & Harga")}
          </Grid>
          <Grid item xs={12}>
            {renderBooleanField("menawarkan_loyalty_event", "Menawarkan Loyalty/Event")}
          </Grid>
          <Grid item xs={12}>{renderPointField("menjelaskan_fitur", "Kemampuan Menjelaskan Fitur")}</Grid>
          <Grid item xs={12}>
            {renderBooleanField("menawarkan_additional_print", "Menawarkan Additional Print")}
          </Grid>
          <Grid item xs={12}>{renderPointField("kejelasan_informasi", "Kejelasan Informasi")}</Grid>
          <Grid item xs={12}>
            {renderTextField(
              "catatan_product_knowledge",
              "Catatan Product Knowledge",
              500,
              true
            )}
          </Grid>
        </Grid>
      );
    }

    if (currentSection === 5) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>{renderPointField("kebersihan_lantai", "Kebersihan Lantai")}</Grid>
          <Grid item xs={12}>{renderPointField("kebersihan_booth", "Kebersihan Booth")}</Grid>
          <Grid item xs={12}>{renderPointField("kebersihan_properti", "Kebersihan Properti")}</Grid>
          <Grid item xs={12}>{renderPointField("kenyamanan_tempat", "Kenyamanan Tempat")}</Grid>
          <Grid item xs={12}>{renderBooleanField("musik", "Musik Dari Photomatics (Jingle)")}
          </Grid>
          <Grid item xs={12}>
            {renderTextField("catatan_kondisi_store", "Catatan Kondisi Store", 500, true)}
          </Grid>
        </Grid>
      );
    }

    if (currentSection === 6) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>{renderPointField("mesin_normal", "Mesin Berfungsi Normal")}</Grid>
          <Grid item xs={12}>{renderPointField("kamera_jelas", "Kamera & Layar Jelas")}</Grid>
          <Grid item xs={12}>{renderPointField("printer_hasil", "Printer Hasil Baik")}</Grid>
          <Grid item xs={12}>{renderBooleanField("error", "Error/Lag")}</Grid>
          <Grid item xs={12}>
            {renderTextField("catatan_equipment", "Catatan Equipment", 500, true)}
          </Grid>
        </Grid>
      );
    }

    if (currentSection === 7) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderPointField("penjelasan_flow_antrian", "Penjelasan Flow Antrian")}
          </Grid>
          <Grid item xs={12}>{renderPointField("waktu_tunggu", "Waktu Tunggu")}</Grid>
          <Grid item xs={12}>{renderPointField("kerapihan_antrian", "Kerapihan Antrian")}</Grid>
          <Grid item xs={12}>{renderPointField("pengaturan_flow", "Pengaturan Flow oleh Crew")}</Grid>
          <Grid item xs={12}>{renderTextField("catatan_antrian", "Catatan Antrian", 500, true)}</Grid>
        </Grid>
      );
    }

    if (currentSection === 8) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderBooleanField("kepuasan_keseluruhan", "Apakah kamu puas secara keseluruhan?")}
          </Grid>
          <Grid item xs={12}>{renderBooleanField("kembali_lagi", "Apakah kamu akan kembali lagi?")}</Grid>
          <Grid item xs={12}>{renderBooleanField("rekomendasi", "Apakah kamu akan merekomendasikan ke orang terdekat kamu?")}</Grid>
        </Grid>
      );
    }

    if (currentSection === 9) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>Hal apa yang membuat kamu atau customer lain tidak akan datang ke cabang ini lagi?</FormLabel>
              <Controller
                name="red_flag"
                control={control}
                rules={{ required: "Wajib diisi" }}
                render={({ field }) => (
                  <TextField select size="small" sx={{ mt: 1 }} {...field}>
                    <MenuItem value="">Pilih</MenuItem>
                    {redFlagOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </FormControl>
          </Grid>

          {redFlag === "Other" && (
            <Grid item xs={12}>
              {renderTextField("red_flag_other", "Other", 20, false, 3, true)}
            </Grid>
          )}
        </Grid>
      );
    }

    if (currentSection === 10) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderTextField(
              "cerita_pengalaman",
              "Ceritakan pengalaman kamu secara detail (Positif & Negatif)",
              1000,
              true,
              6
            )}
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Foto Crew (Maksimal 5)</FormLabel>
            <Button component="label" variant="outlined" sx={{ mt: 1, width: "fit-content" }}>
              Upload Foto Crew
              <input
                hidden
                type="file"
                multiple
                onChange={(event) => handleFileChange(event, "foto_crew", 5)}
              />
            </Button>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {fotoCrew.length} file dipilih
            </Typography>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Foto Store (Maksimal 10)</FormLabel>
            <Button component="label" variant="outlined" sx={{ mt: 1, width: "fit-content" }}>
              Upload Foto Store
              <input
                hidden
                type="file"
                multiple
                onChange={(event) => handleFileChange(event, "foto", 10)}
              />
            </Button>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {fotoStore.length} file dipilih
            </Typography>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Upload Voice (Crew Menawarkan Additional)</FormLabel>
            <Button component="label" variant="outlined" sx={{ mt: 1, width: "fit-content" }}>
              Upload Voice
              <input
                hidden
                type="file"
                onChange={(event) => handleFileChange(event, "voice_additional", 1)}
              />
            </Button>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {voiceAdditional.length} file dipilih
            </Typography>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Upload Video Jingle</FormLabel>
            <Button component="label" variant="outlined" sx={{ mt: 1, width: "fit-content" }}>
              Upload Video
              <input
                hidden
                type="file"
                onChange={(event) => handleFileChange(event, "video_jingle", 1)}
              />
            </Button>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {videoJingle.length} file dipilih
            </Typography>
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  if (isSubmitted) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          background:
            "radial-gradient(circle at top right, rgba(255,200,150,0.35), transparent 40%), linear-gradient(180deg, #f9fbff 0%, #f2f6fb 100%)",
        }}
      >
        <Helmet>
          <title>Mystery Shopper Form - Photomatics</title>
        </Helmet>

        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            textAlign: "center",
            color: "#1f2a37",
            maxWidth: 800,
          }}
        >
          Terima Kasih Mystery Shopper!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 6 },
        px: 2,
        background:
          "radial-gradient(circle at top left, rgba(124, 182, 255, 0.2), transparent 45%), radial-gradient(circle at bottom right, rgba(255, 199, 145, 0.25), transparent 45%), linear-gradient(180deg, #f8fbff 0%, #eff4fa 100%)",
      }}
    >
      <Helmet>
        <title>Mystery Shopper Form - Photomatics</title>
      </Helmet>

      <Box sx={{ maxWidth: 980, mx: "auto" }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 900,
            letterSpacing: 0.4,
            textAlign: "center",
            color: "#192231",
          }}
        >
          MYSTERY SHOPPER FORM - PHOTOMATICS
        </Typography>

        <Paper
          elevation={0}
          sx={{
            mt: 3,
            borderRadius: 3,
            p: { xs: 2, md: 4 },
            border: "1px solid #dbe6f3",
            backgroundColor: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(6px)",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Section {currentSection + 1}: {sectionTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentSection + 1}/{totalSections}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={((currentSection + 1) / totalSections) * 100}
              sx={{ mt: 1, height: 8, borderRadius: 999 }}
            />
          </Box>

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            {renderSectionContent()}

            <Stack direction="row" spacing={1.5} sx={{ mt: 4 }}>
              {currentSection > 0 && (
                <Button variant="outlined" onClick={goBack}>
                  Back
                </Button>
              )}

              {currentSection < totalSections - 1 && (
                <Button variant="contained" onClick={goNext}>
                  Next
                </Button>
              )}

              {currentSection === totalSections - 1 && (
                <LoadingButton
                  variant="contained"
                  type="submit"
                  loading={submitMutation.isLoading}
                >
                  Submit
                </LoadingButton>
              )}
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
